import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { filterOutIgnoredAccounts, filterOutComputedAccounts } from '@algoan/dashboard/utils';
import { Account, AccountType, AsyncStatus } from '@algoan/dashboard/model';
import { updateAuthInformation } from './auth-slice';

export type AccountErrorMessages = 'noAccountsError' | 'genericError';

export interface AccountReducerState {
  accounts: Account[];
  ignoredAccounts: Account[];
  accountsFetchingStatus: AsyncStatus;
  accountsError: AccountErrorMessages | undefined;
  selectedAccounts: {
    bankHistory?: string;
    balances?: string;
  };
  checkedAccounts: CheckedAccount[];
}

export interface CheckedAccount {
  id: string;
  name: string;
  numberOfTransactions: number;
  type: AccountType;
}

const getInitialState = (): AccountReducerState => ({
  accounts: [],
  ignoredAccounts: [],
  accountsFetchingStatus: AsyncStatus.IDLE,
  accountsError: undefined,
  selectedAccounts: {},
  checkedAccounts: [],
});

const initialState = getInitialState();

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    fetchAccountsStart(state) {
      state.accountsFetchingStatus = AsyncStatus.PENDING;
      state.accountsError = undefined;
    },
    fetchAccountsSuccess(state, action: PayloadAction<Account[]>) {
      const allAccounts = action.payload.map((account, index) => ({ ...account, originalIndex: index }));

      state.accounts = filterOutIgnoredAccounts(allAccounts);
      state.ignoredAccounts = filterOutComputedAccounts(allAccounts);
      state.accountsFetchingStatus = AsyncStatus.RESOLVED;
      state.accountsError = undefined;
    },
    fetchAccountsFailure(state, action: PayloadAction<{ error: AccountErrorMessages }>) {
      state.accountsFetchingStatus = AsyncStatus.REJECTED;
      state.accountsError = action.payload.error;
    },
    resetAccountState: () => initialState,
    selectAccount(state, action: PayloadAction<{ type: 'bankHistory' | 'balances'; value: string | undefined }>) {
      const { type, value } = action.payload;

      if (state.selectedAccounts[type] === value && value !== undefined) {
        state.selectedAccounts[type] = undefined;
        return;
      }

      state.selectedAccounts[type] = value;
    },
    initializeCheckedAccounts(state, action: PayloadAction<CheckedAccount[]>) {
      state.checkedAccounts = action.payload;
    },

    deleteCheckedAccounts(state) {
      state.checkedAccounts = [];
    },

    toggleAccount(state, action: PayloadAction<CheckedAccount>) {
      const index = state.checkedAccounts.map((account) => account.id).indexOf(action.payload.id);
      const isAlreadyChecked = state.checkedAccounts.some((account) => account.id === action.payload.id);
      if (isAlreadyChecked) {
        state.checkedAccounts.splice(index, 1);
      } else state.checkedAccounts.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateAuthInformation, (state, action) => {
      if (typeof action.payload === 'undefined') {
        // we clear the state on logout
        return getInitialState();
      }

      return state;
    });
  },
});

export const { reducer: accountReducer } = accountSlice;
export const {
  fetchAccountsFailure,
  fetchAccountsStart,
  fetchAccountsSuccess,
  resetAccountState,
  selectAccount,
  toggleAccount,
  initializeCheckedAccounts,
  deleteCheckedAccounts,
} = accountSlice.actions;
